import { optional, prefersReducedMotion } from '@/lib/dom'

const LOADED_CLASS = 'is-loaded'

/** 同一セッションで一度演出を見せたら、以降はスキップする（LCP対策）。
 *  head のインラインスクリプトが同じキーを見て、描画前に幕ごと非表示にする */
const SEEN_KEY = 'intro-seen'

function hasSeenIntro(): boolean {
  try {
    return sessionStorage.getItem(SEEN_KEY) === '1'
  } catch {
    return false
  }
}

function markIntroSeen(): void {
  try {
    sessionStorage.setItem(SEEN_KEY, '1')
  } catch {
    /* プライベートモード等で使えなくても演出が二重に出るだけ */
  }
}

/**
 * ローディング幕（.js-loading-wipe）の解除。
 *
 * 幕は CSS の初期状態で画面全体を覆っていて（黒の下に青、中央にワードマーク）、
 * これがそのままローディング UI になっている。ページの読み込みが終わったら
 *   ワードマークが上に抜ける → 黒が上へ開く → 一拍遅れて青が追う
 * の順で本体を見せる。
 *
 * prepare は「幕がまだ画面を覆っているうち」に呼ばれる。
 * スクロール演出の初期状態（opacity: 0 など）をここで敷いておかないと、
 * 幕が開いたあとに要素が消えて再登場する二重描画が見えてしまう。
 */
export function initLoading(prepare?: () => void): Promise<void> {
  const transLayer = optional('#transLayer')

  const reveal = (): void => {
    document.body.classList.add(LOADED_CLASS)
    transLayer?.classList.remove('is-loading')
  }

  // 2 回目以降の訪問。幕は head のインラインスクリプトが描画前に消しているので、
  // ここでは本体の表示状態を整えるだけでよい
  if (hasSeenIntro()) {
    const wipe = optional('.js-loading-wipe')
    if (wipe) wipe.style.display = 'none'
    prepare?.()
    reveal()
    return Promise.resolve()
  }

  return new Promise<void>((resolve) => {
    let started = false
    const finish = (): void => {
      if (started) return
      started = true
      markIntroSeen()

      const wipe = optional('.js-loading-wipe')

      // 幕の開閉そのものは CSS アニメーション（sections.css の wipe-open）が
      // 担う。CSS はスタイル適用直後から JS を待たずに自走するので、
      // JS の到着が遅い回線でも幕が本体を隠し続けることがない。
      // JS 側の仕事は、幕がまだ視界を覆っているうちに演出の初期状態を
      // 敷き（prepare）、本体を表示状態にし（reveal）、開き切った幕を
      // DOM から外すことだけ
      prepare?.()
      reveal()
      resolve()

      if (wipe) {
        if (prefersReducedMotion()) {
          wipe.style.display = 'none'
        } else {
          // 幕が視界の外へスライドしても、fixed のコンテナが残っている間は
          // ブラウザが FCP/LCP を記録しないことがある。CSS アニメーションが
          // 終わった瞬間に DOM から外す（タイマーは保険）
          const hide = (): void => {
            wipe.style.display = 'none'
          }
          const accent = optional('.js-loading-wipe-accent')
          accent?.addEventListener('animationend', hide, { once: true })
          window.setTimeout(hide, 2000)
        }
      }
    }

    // window の load（全画像の完了）は待たない。待つとその間ずっと幕が
    // 本体を隠し、FCP/LCP がまるごと遅延する。DOM さえ揃えば本体を出してよく、
    // 画像は preload 済みの MV から順に幕が開く頃には概ね描画できている
    if (document.readyState !== 'loading') {
      finish()
    } else {
      document.addEventListener('DOMContentLoaded', finish, { once: true })
    }
  })
}
